/**
 * @file AddEditPlanDetails.js
 * @description A component to add or edit a Plan Detail.
 */
import React, { useState, useEffect } from 'react';
import {
    createPlanDetail,
    updatePlanDetail,
} from '../../services/systemService';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { useNavigate, useParams } from 'react-router';
import { Dropdown } from '../helpers/Dropdown';
import {
    fetchPlanDetailPlanTypes,
    fetchPlanDetailById,
} from '../../services/systemService';
import styles from './PlanDetails.module.scss';
import { ROUTES } from '../../constants/routes';
import { useAuthContext } from '../../store/Contexts';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

const AddEditPlanDetails = () => {
    const navigate = useNavigate();
    const { planDetailId } = useParams();
    const isNewPlanDetail = location.pathname.includes('add');
    const [isLoading, setIsLoading] = useState(!isNewPlanDetail ?? true);
    const [planDetail, setPlanDetail] = useState({
        code: '',
        planTypeId: '',
        category: '',
        name: '',
    });
    const [planTypeOptions, setPlanTypeOptions] = useState([]);

    const { hasAttribute } = useAuthContext();

    const canEditForm = hasAttribute(SecurityAttributes.PlanDetailFullAccess);

    const handlePlanDetailPlanTypeChange = newPlanTypeId => {
        setPlanDetail(prevDetails => ({
            ...prevDetails,
            planTypeId: newPlanTypeId,
        }));
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setPlanDetail(prevDetails => ({ ...prevDetails, [name]: value }));
    };

    const handleClose = () => {
        navigate(ROUTES.PLAN_DETAILS);
    };

    const validateForm = () => {
        if (!planDetail.code || planDetail.code.trim() === '') {
            Toast.error(getText('msg_ea6ac41a6ab6'));
            return false;
        }
        if (!planDetail.planTypeId) {
            Toast.error(getText('msg_bd01e410d726'));
            return false;
        }
        if (!planDetail.category || planDetail.category === '') {
            Toast.error(getText('msg_25c854effb16'));
            return false;
        }
        if (!planDetail.name || planDetail.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        try {
            const planDetailPayload = {
                id: isNewPlanDetail ? 0 : planDetail.id,
                planTypeId: planDetail.planTypeId,
                name: planDetail.name,
                code: planDetail.code,
                category: planDetail.category,
            };

            if (isNewPlanDetail) {
                await createPlanDetail(planDetailPayload);
                Toast.success(getText('msg_cc51ecec5304'));
            } else {
                await updatePlanDetail(planDetailPayload);
                Toast.success(getText('msg_ed5760f85508'));
            }

            navigate(ROUTES.PLAN_DETAILS);
        } catch (error) {
            console.error('Error saving plan details:', error);
            Toast.error(getText('msg_278a4e032727'));
        }
    };

    // Find the plan type label for the case of the user editing an existing plan detail
    const getPlanDetailPlanType = () => {
        if (planTypeOptions.length > 0 && planDetail.planTypeId) {
            const matchedPlanType = planTypeOptions.find(
                option => option.id === planDetail.planTypeId,
            );
            return matchedPlanType ? matchedPlanType.label : 'Select Option';
        }
        return 'Select Option';
    };

    // Fetch all plan types
    const loadPlanTypes = async () => {
        try {
            const fetchedPlanTypes = await fetchPlanDetailPlanTypes();
            const fetchedPlanTypeOptionsToSet = fetchedPlanTypes.map(type => ({
                label: type.name,
                id: type.id,
            }));
            setPlanTypeOptions(fetchedPlanTypeOptionsToSet);
        } catch (error) {
            console.error('Failed to get Plan Types.', error);
            Toast.error(getText('msg_4506974a3c66'));
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch an individual plan detail by id
    const fetchPlanDetailToEdit = async () => {
        try {
            const planDetailToEdit = await fetchPlanDetailById(planDetailId);
            setPlanDetail({
                code: planDetailToEdit.code,
                planTypeId: planDetailToEdit.planTypeId,
                category: planDetailToEdit.category,
                name: planDetailToEdit.name,
                id: planDetailToEdit.id,
            });
        } catch (error) {
            console.error('Failed to fetch individual Plan Detail.', error);
            Toast.error(
                getText('msg_492f9d3ac5cc'),
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPlanTypes();
        if (!isNewPlanDetail && planDetailId) {
            fetchPlanDetailToEdit();
        }
    }, [isNewPlanDetail, planDetailId]);

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4 b">
                    <div className="d-flex justify-content-between align-items-center pb-3">
                        <p className="page-title float-start card-title ms-4 mb-0">
                            {isNewPlanDetail
                                ? 'Add Plan Detail'
                                : `Edit Plan Detail`}
                        </p>
                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={handleClose}
                                className="ing-text-buttons mx-4"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    <form
                        onSubmit={e => e.preventDefault()}
                        className={`m-auto col-xs-12 col-sm-12 col-md-10 col-lg-8 px-4 py-5`}
                    >
                        <section className="card-title d-flex flex-wrap mb-5 overflow-visible flex-column">
                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="code">{getText('msg_d047c83278d1')}</label>
                                <span className="text-danger">*</span>
                                <input
                                    id="code"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={planDetail.code}
                                    onChange={handleChange}
                                    name="code"
                                    placeholder={getText('msg_601202604470')}
                                    required
                                />
                            </div>

                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="code">{getText('msg_45061ce8ef30')}</label>
                                <span className="text-danger">*</span>
                                <Dropdown
                                    defaultValue={getPlanDetailPlanType}
                                    onChange={option => {
                                        handlePlanDetailPlanTypeChange(option);
                                    }}
                                    options={planTypeOptions}
                                    useOnChangeValueFallback={true}
                                    isTitleEnabled={true}
                                />
                            </div>
                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="category">{getText('msg_a3c686e711e4')}</label>
                                <span className="text-danger">*</span>
                                <input
                                    id="category"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={planDetail.category}
                                    onChange={handleChange}
                                    name="category"
                                    placeholder={getText('msg_c571d3ec6ec7')}
                                    required
                                />
                            </div>

                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="name">{getText('msg_ecdda59aea5e')}</label>
                                <span className="text-danger">*</span>
                                <input
                                    id="name"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={planDetail.name}
                                    onChange={handleChange}
                                    name="name"
                                    placeholder={getText('msg_c571d3ec6ec7')}
                                    required
                                />
                            </div>
                        </section>
                        <div className="d-flex justify-content-between">
                            <span
                                onClick={handleClose}
                                type="button"
                                className="ms-3 button-base ing-button-primary-left align-left"
                            >
                                {getText('msg_77dfd2135f4d')}
                            </span>
                            {canEditForm && (
                                <span
                                    onClick={handleSave}
                                    type="button"
                                    className="ms-3 button-base ing-button-primary-left align-middle"
                                >
                                    {getText('msg_efc007a393f6')}
                                </span>
                            )}
                        </div>
                        <p className={styles.requiredFieldContainer}>
                            <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddEditPlanDetails;
